import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../contexts/AuthContext';

interface InvitationRow {
  id: string;
  token: string;
  email: string | null;
  created_at: string;
  expires_at: string;
  used_at: string | null;
  used_by: string | null;
  redeemed_email: string | null;
  notes: string | null;
}

type InvitationStatus = 'active' | 'used' | 'expired';

function computeStatus(inv: InvitationRow): InvitationStatus {
  if (inv.used_at) return 'used';
  if (new Date(inv.expires_at) <= new Date()) return 'expired';
  return 'active';
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function AdminInvites() {
  const { user, profile, loading: authLoading } = useAuth();
  const [invitations, setInvitations] = useState<InvitationRow[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  // Create form state
  const [mode, setMode] = useState<'email' | 'open'>('email');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [expiryDays, setExpiryDays] = useState('7');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [createdInviteEmail, setCreatedInviteEmail] = useState<string | null>(null);
  const [showOpenConfirm, setShowOpenConfirm] = useState(false);

  const [copiedField, setCopiedField] = useState<string | null>(null);

  const isAdmin = !!user && !!profile?.is_admin;

  const loadInvitations = useCallback(async () => {
    if (!user || !profile?.is_admin) {
      setInvitations([]);
      setLoadingList(false);
      return;
    }
    setLoadingList(true);
    setListError(null);
    const { data, error } = await supabase
      .from('invitations')
      .select('id, token, email, created_at, expires_at, used_at, used_by, redeemed_email, notes')
      .order('created_at', { ascending: false });
    if (error) {
      setListError('Unable to load invitations.');
      setInvitations([]);
    } else {
      setInvitations((data || []) as InvitationRow[]);
    }
    setLoadingList(false);
  }, [user, profile]);

  useEffect(() => {
    if (!authLoading) {
      loadInvitations();
    }
  }, [authLoading, loadInvitations]);

  const copyToClipboard = async (text: string, fieldKey: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldKey);
      setTimeout(() => setCopiedField(null), 1800);
    } catch {
      // Clipboard API may be unavailable; ignore silently.
    }
  };

  const handleCreate = async (confirmedOpen = false) => {
    setCreateError(null);
    setCreatedToken(null);
    setCreatedInviteEmail(null);

    if (mode === 'email') {
      const trimmed = email.trim();
      if (!trimmed) {
        setCreateError('Please enter an email address.');
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmed)) {
        setCreateError('Please enter a valid email address.');
        return;
      }
    } else {
      // Open invitation requires deliberate confirmation.
      if (!confirmedOpen) {
        setShowOpenConfirm(true);
        return;
      }
      setShowOpenConfirm(false);
    }

    const days = parseInt(expiryDays, 10);
    if (isNaN(days) || days < 1) {
      setCreateError('Expiry must be at least 1 day.');
      return;
    }
    if (days > 365) {
      setCreateError('Expiry cannot exceed 365 days.');
      return;
    }

    const expiresAt = new Date(Date.now() + days * 86400000).toISOString();
    const pEmail = mode === 'email' ? email.trim() : null;
    const pNotes = notes.trim() || null;

    setCreating(true);
    const { data, error } = await supabase.rpc('create_invitation', {
      p_email: pEmail,
      p_expires_at: expiresAt,
      p_notes: pNotes,
    });

    setCreating(false);

    if (error) {
      const msg = (error.message || '').toLowerCase();
      if (msg.includes('not authorized') || msg.includes('not an admin')) {
        setCreateError('You are not authorised to create invitations.');
      } else if (msg.includes('expiry')) {
        setCreateError('The expiry value was rejected. Please choose a valid expiry (1–365 days).');
      } else if (msg.includes('unique') || msg.includes('duplicate') || msg.includes('already')) {
        setCreateError('An active invitation already exists for this email.');
      } else {
        setCreateError('Could not create the invitation. Please try again.');
      }
      return;
    }

    if (!data || typeof data !== 'string') {
      setCreateError('Invitation creation returned an unexpected response.');
      return;
    }

    setCreatedToken(data);
    setCreatedInviteEmail(pEmail);
    setEmail('');
    setNotes('');
    setExpiryDays('7');
    setMode('email');
    await loadInvitations();
  };

  // --- Access control states ---

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-slate-400">Loading…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="mb-4 text-5xl">🔒</div>
          <h1 className="text-2xl font-bold mb-2">Not Authorised</h1>
          <p className="text-slate-400 mb-6">Please sign in to access this area.</p>
          <button
            onClick={() => { window.location.href = '/'; }}
            className="bg-slate-700 hover:bg-slate-600 text-white font-semibold py-2.5 px-6 rounded-lg transition-colors"
          >
            Back to WatchWicket
          </button>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="mb-4 text-5xl">🚫</div>
          <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
          <p className="text-slate-400 mb-6">You do not have permission to view this page.</p>
          <button
            onClick={() => { window.location.href = '/'; }}
            className="bg-slate-700 hover:bg-slate-600 text-white font-semibold py-2.5 px-6 rounded-lg transition-colors"
          >
            Back to WatchWicket
          </button>
        </div>
      </div>
    );
  }

  const inviteLink = createdToken
    ? `${window.location.origin}/invite/${createdToken}`
    : '';

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <header className="border-b border-slate-800 sticky top-0 bg-slate-900/95 backdrop-blur z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Invite Management</h1>
            <p className="text-xs text-slate-400 mt-0.5">WatchWicket private beta</p>
          </div>
          <button
            onClick={() => { window.location.href = '/'; }}
            className="text-sm text-slate-400 hover:text-white transition-colors"
          >
            Exit
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Create invitation card */}
        <section className="bg-slate-800 rounded-xl p-5">
          <h2 className="text-lg font-bold mb-4">Create Invitation</h2>

          {/* Mode toggle */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => { setMode('email'); setCreateError(null); }}
              className={`flex-1 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                mode === 'email'
                  ? 'bg-green-600 text-white'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              Email-bound
            </button>
            <button
              onClick={() => { setMode('open'); setCreateError(null); }}
              className={`flex-1 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                mode === 'open'
                  ? 'bg-amber-600 text-white'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              Open link
            </button>
          </div>

          {mode === 'open' && (
            <div className="mb-4 bg-amber-900/30 border border-amber-700/50 rounded-lg p-3">
              <p className="text-sm text-amber-200">
                Anyone who has this link or code can create an account. Use open invitations carefully.
              </p>
            </div>
          )}

          <div className="space-y-4">
            {mode === 'email' && (
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Email address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="player@example.com"
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-green-500"
                  autoComplete="off"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">
                Expires in (days)
              </label>
              <input
                type="number"
                min="1"
                max="365"
                value={expiryDays}
                onChange={(e) => setExpiryDays(e.target.value)}
                className="w-32 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-green-500"
              />
              <p className="text-xs text-slate-500 mt-1">Default: 7 days. Max: 365 days.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">
                Internal notes <span className="text-slate-500">(optional)</span>
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. Coach for Sunday league"
                rows={2}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-green-500 resize-none"
              />
            </div>

            {createError && (
              <div className="bg-red-900/40 border border-red-700/50 rounded-lg p-3">
                <p className="text-sm text-red-200">{createError}</p>
              </div>
            )}

            {mode === 'open' && showOpenConfirm ? (
              <div className="bg-amber-900/30 border border-amber-700/50 rounded-lg p-4">
                <p className="text-sm text-amber-100 mb-3 font-medium">
                  Confirm: create an open invitation that anyone with the link can use?
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => handleCreate(true)}
                    disabled={creating}
                    className="bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white font-semibold py-2 px-4 rounded-lg text-sm transition-colors"
                  >
                    {creating ? 'Creating…' : 'Yes, create open invite'}
                  </button>
                  <button
                    onClick={() => setShowOpenConfirm(false)}
                    className="bg-slate-700 hover:bg-slate-600 text-white font-semibold py-2 px-4 rounded-lg text-sm transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => handleCreate(false)}
                disabled={creating}
                className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold py-3 rounded-lg transition-colors"
              >
                {creating ? 'Creating…' : mode === 'email' ? 'Create email invitation' : 'Create open invitation'}
              </button>
            )}
          </div>

          {/* Created invitation result */}
          {createdToken && (
            <div className="mt-5 bg-green-900/30 border border-green-700/50 rounded-lg p-4 space-y-3">
              <p className="text-sm text-green-200 font-medium">
                Invitation created{createdInviteEmail ? ` for ${createdInviteEmail}` : ''}.
              </p>
              <div>
                <p className="text-xs text-slate-400 mb-1">Invitation code</p>
                <div className="flex gap-2">
                  <code className="flex-1 bg-slate-900 rounded px-3 py-2 text-sm text-green-300 break-all font-mono">
                    {createdToken}
                  </code>
                  <button
                    onClick={() => copyToClipboard(createdToken, 'createdCode')}
                    className="bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium px-3 rounded-lg transition-colors whitespace-nowrap"
                  >
                    {copiedField === 'createdCode' ? 'Copied!' : 'Copy Code'}
                  </button>
                </div>
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-1">Invitation link</p>
                <div className="flex gap-2">
                  <code className="flex-1 bg-slate-900 rounded px-3 py-2 text-sm text-slate-300 break-all font-mono">
                    {inviteLink}
                  </code>
                  <button
                    onClick={() => copyToClipboard(inviteLink, 'createdLink')}
                    className="bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium px-3 rounded-lg transition-colors whitespace-nowrap"
                  >
                    {copiedField === 'createdLink' ? 'Copied!' : 'Copy Link'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Invitation list */}
        <section className="bg-slate-800 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold">Invitations</h2>
            <button
              onClick={loadInvitations}
              disabled={loadingList}
              className="text-sm text-slate-400 hover:text-white transition-colors"
            >
              {loadingList ? 'Loading…' : 'Refresh'}
            </button>
          </div>

          {listError && (
            <div className="bg-red-900/40 border border-red-700/50 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-200">{listError}</p>
            </div>
          )}

          {!listError && !loadingList && invitations.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-8">
              No invitations yet. Create one above.
            </p>
          )}

          {invitations.length > 0 && (
            <div className="space-y-3">
              {invitations.map((inv) => {
                const status = computeStatus(inv);
                const link = `${window.location.origin}/invite/${inv.token}`;
                return (
                  <div
                    key={inv.id}
                    className="bg-slate-900 rounded-lg p-4 border border-slate-700/50"
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-white truncate">
                          {inv.email ? inv.email : 'Open invitation'}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Created {formatDate(inv.created_at)} · Expires {formatDate(inv.expires_at)}
                        </p>
                      </div>
                      <StatusBadge status={status} />
                    </div>

                    {status === 'used' && inv.redeemed_email && (
                      <p className="text-xs text-slate-400 mb-2">
                        Redeemed by {inv.redeemed_email} on {formatDate(inv.used_at)}
                      </p>
                    )}

                    {inv.notes && (
                      <p className="text-xs text-slate-400 italic mb-2">
                        Note: {inv.notes}
                      </p>
                    )}

                    {status === 'active' && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        <button
                          onClick={() => copyToClipboard(link, `link-${inv.id}`)}
                          className="bg-slate-700 hover:bg-slate-600 text-white text-xs font-medium px-3 py-1.5 rounded-md transition-colors"
                        >
                          {copiedField === `link-${inv.id}` ? 'Link copied!' : 'Copy Link'}
                        </button>
                        <button
                          onClick={() => copyToClipboard(inv.token, `code-${inv.id}`)}
                          className="bg-slate-700 hover:bg-slate-600 text-white text-xs font-medium px-3 py-1.5 rounded-md transition-colors"
                        >
                          {copiedField === `code-${inv.id}` ? 'Code copied!' : 'Copy Code'}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function StatusBadge({ status }: { status: InvitationStatus }) {
  const styles: Record<InvitationStatus, string> = {
    active: 'bg-green-900/50 text-green-300 border-green-700/50',
    used: 'bg-slate-700 text-slate-300 border-slate-600',
    expired: 'bg-red-900/50 text-red-300 border-red-700/50',
  };
  const labels: Record<InvitationStatus, string> = {
    active: 'Active',
    used: 'Used',
    expired: 'Expired',
  };
  return (
    <span className={`text-xs font-medium px-2.5 py-1 rounded-full border whitespace-nowrap ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}
